// FILE: chatPanelPersistence.ts
// Purpose: Sanitize right-panel state read back from localStorage so an unknown panel value
//   cannot open a panel that nothing knows how to render.
// Layer: UI state store helpers
// Exports: shared panel-state shape, its default, and the sanitizer used by both panel stores.

import { ThreadId, TurnId } from "@taskin-code/contracts";
import { isChatRightPanel, type ChatRightPanel } from "./diffRouteSearch";

// The single-thread surface and each split-view pane persist the exact same shape. Keeping one
// declaration means a new field can only be added in one place, and both stores sanitize alike.
export interface ChatPanelStateFields {
  panel: ChatRightPanel | null;
  diffTurnId: TurnId | null;
  diffFilePath: string | null;
  hasOpenedPanel: boolean;
  lastOpenPanel: ChatRightPanel;
  /**
   * The side chat this surface last had open, so closing and reopening the panel
   * returns to that conversation instead of starting another one. Persisted
   * because the thread outlives the panel: it is a real thread on the server, and
   * a fresh one per reopen would litter the sidebar with abandoned children.
   */
  sideChatThreadId: ThreadId | null;
}

/**
 * The part of a surface's panel state the panels themselves read and patch.
 *
 * Named rather than re-`Pick`ed at each call site: five signatures pass this slice
 * down (the diff panel, the side chat, and the three surfaces that render them),
 * and spelling it out at each one meant a new field had to be added five times,
 * with the ones missed only failing where a panel happened to require it.
 */
export type ChatRightPanelSlice = Pick<
  ChatPanelStateFields,
  "panel" | "diffTurnId" | "diffFilePath" | "sideChatThreadId"
>;

export const DEFAULT_LAST_OPEN_PANEL: ChatRightPanel = "browser";

export function createDefaultChatPanelState(): ChatPanelStateFields {
  return {
    panel: null,
    diffTurnId: null,
    diffFilePath: null,
    hasOpenedPanel: false,
    lastOpenPanel: DEFAULT_LAST_OPEN_PANEL,
    sideChatThreadId: null,
  };
}

// Returns null when the entry is too malformed to keep, so callers can drop just that entry
// instead of discarding every thread's panel state.
export function sanitizeChatPanelState(value: unknown): ChatPanelStateFields | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const candidate = value as Record<string, unknown>;

  // An unknown `panel` must collapse to null rather than to a fallback panel: a rolled-back
  // client that kept rendering the value it no longer understands would otherwise show an
  // opened panel with no matching content and no way to toggle it shut.
  const panel = isChatRightPanel(candidate.panel) ? candidate.panel : null;
  // `lastOpenPanel` is what reopening the panel replays, so a stale value here is the one that
  // survives every close/open cycle. It has to land on a panel that still exists.
  const lastOpenPanel = isChatRightPanel(candidate.lastOpenPanel)
    ? candidate.lastOpenPanel
    : DEFAULT_LAST_OPEN_PANEL;
  const diffTurnId =
    typeof candidate.diffTurnId === "string" && candidate.diffTurnId.length > 0
      ? TurnId.makeUnsafe(candidate.diffTurnId)
      : null;

  return {
    panel,
    diffTurnId,
    diffFilePath: typeof candidate.diffFilePath === "string" ? candidate.diffFilePath : null,
    hasOpenedPanel: candidate.hasOpenedPanel === true,
    lastOpenPanel,
    sideChatThreadId:
      typeof candidate.sideChatThreadId === "string" && candidate.sideChatThreadId.length > 0
        ? ThreadId.makeUnsafe(candidate.sideChatThreadId)
        : null,
  };
}
